import React, { useState, useEffect, useCallback } from "react";
import { Button, Dropdown, Icon, Loader, Popup, Radio, Table } from "semantic-ui-react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { DataIntegrity, Site } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { useCompany } from "../../../../contexts/CompanyContext";
import { isMaster } from "../../../../utils/checkPermission";
import "./DuplicatePeriodsReview.scss";

const dataIntegrityController = new DataIntegrity();
const siteController = new Site();

function formatDate(date) {
  if (!date) return "-";
  return new Date(date).toLocaleString();
}

// Para un grupo de duplicados y el documento elegido para conservar, determina
// qué campos se van a completar automáticamente (el keeper no lo tiene, y
// todos los demás documentos que sí lo tienen coinciden en el valor) y cuáles
// son conflictos reales (el mismo campo con valores distintos entre documentos).
function computeMergePreview(group, keepId) {
  const fieldOccurrences = {}; // fieldName -> [{docId, value, unit}]
  group.documents.forEach((doc) => {
    doc.fields.forEach((f) => {
      if (!fieldOccurrences[f.name]) fieldOccurrences[f.name] = [];
      fieldOccurrences[f.name].push({ docId: doc.id, value: f.value, unit: f.unit });
    });
  });

  const toMerge = [];
  const conflicts = [];

  Object.entries(fieldOccurrences).forEach(([fieldName, occurrences]) => {
    const keeperOcc = occurrences.find((o) => o.docId === keepId);
    const others = occurrences.filter((o) => o.docId !== keepId);

    if (!keeperOcc) {
      const uniqueValues = [...new Set(others.map((o) => o.value))];
      if (uniqueValues.length === 1) {
        toMerge.push({ field: fieldName, value: others[0].value, unit: others[0].unit, fromDocId: others[0].docId });
      } else {
        conflicts.push({ field: fieldName, options: others });
      }
    } else {
      const differing = others.filter((o) => o.value !== keeperOcc.value);
      if (differing.length > 0) {
        conflicts.push({ field: fieldName, options: [keeperOcc, ...differing] });
      }
    }
  });

  return { toMerge, conflicts };
}

export function DuplicatePeriodsReview() {
  const { accessToken, user } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const { company: companyContext } = useCompany();
  // Master ve todo salvo que esté "parado" en una empresa puntual (selector del header)
  const masterCompanyScope = isMaster(user?.role) ? companyContext : "";

  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(""); // "" = todos los sitios (dentro del alcance)
  const [groups, setGroups] = useState([]);
  const [keepSelection, setKeepSelection] = useState({}); // groupKey -> id a conservar
  const [conflictResolutions, setConflictResolutions] = useState({}); // groupKey -> { fieldName: {value, unit} }
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const groupKey = (g) => `${g.module}_${g.site}_${g.year}_${g.period}`;

  useEffect(() => {
    (async () => {
      try {
        const response = masterCompanyScope
          ? await siteController.getSitesByCompany(accessToken, masterCompanyScope, true)
          : await siteController.getSites(accessToken, true);
        setSites(Array.isArray(response) ? response : []);
      } catch (error) {
        console.error(error);
        setSites([]);
      }
    })();
  }, [accessToken, masterCompanyScope]);

  // Si master cambia de empresa parada, el sitio elegido puede ya no aplicar
  useEffect(() => {
    setSelectedSite("");
  }, [masterCompanyScope]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dataIntegrityController.getDuplicatePeriods(
        accessToken,
        selectedSite || undefined,
        masterCompanyScope || undefined,
      );
      const loadedGroups = response.groups || [];
      setGroups(loadedGroups);

      // Preseleccionar el más completo (ya viene ordenado primero desde el server)
      const defaults = {};
      loadedGroups.forEach((g) => {
        defaults[groupKey(g)] = g.documents[0]?.id;
      });
      setKeepSelection(defaults);
      setConflictResolutions({});
    } catch (error) {
      console.error(error);
      toast.error(t("error_loading_duplicates") || "Error al cargar duplicados");
    } finally {
      setLoading(false);
    }
  }, [accessToken, selectedSite, masterCompanyScope]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSelectKeep = (group, docId) => {
    const gk = groupKey(group);
    setKeepSelection((prev) => ({ ...prev, [gk]: docId }));
    // Al cambiar el "conservar" de un grupo, las resoluciones de conflicto
    // anteriores ya no aplican necesariamente -- se recalculan desde cero
    setConflictResolutions((prev) => {
      const next = { ...prev };
      delete next[gk];
      return next;
    });
  };

  const handleResolveConflict = (group, fieldName, option) => {
    const gk = groupKey(group);
    setConflictResolutions((prev) => ({
      ...prev,
      [gk]: { ...(prev[gk] || {}), [fieldName]: { value: option.value, unit: option.unit } },
    }));
  };

  const handleDelete = async () => {
    const payloadGroups = [];

    groups.forEach((group) => {
      const gk = groupKey(group);
      const keepId = keepSelection[gk];
      const deleteIds = group.documents.filter((d) => d.id !== keepId).map((d) => d.id);
      if (deleteIds.length === 0) return;

      const { conflicts } = computeMergePreview(group, keepId);
      const fieldResolutions = {};
      conflicts.forEach((c) => {
        const keeperOption = c.options.find((o) => o.docId === keepId);
        const chosen = conflictResolutions[gk]?.[c.field] || keeperOption || c.options[0];
        fieldResolutions[c.field] = { value: chosen.value, unit: chosen.unit };
      });

      payloadGroups.push({ module: group.module, keepId, deleteIds, fieldResolutions });
    });

    if (payloadGroups.length === 0) {
      toast.warning(t("no_duplicates_to_delete") || "No hay nada para borrar");
      return;
    }

    setDeleting(true);
    try {
      const response = await dataIntegrityController.deleteDuplicatePeriods(accessToken, payloadGroups);
      toast.success(
        `${response.deleted} ${t("duplicates_deleted") || "duplicados borrados"}` +
          (response.mergedFields > 0
            ? `, ${response.mergedFields} ${t("fields_completed") || "campo(s) completado(s)"}`
            : ""),
      );
      if (response.warnings?.length > 0) {
        response.warnings.forEach((w) => toast.warning(w.msg));
      }
      await load();
    } catch (error) {
      console.error(error);
      toast.error(t("error_deleting_duplicates") || "Error al borrar duplicados");
    } finally {
      setDeleting(false);
    }
  };

  const totalToDelete = groups.reduce((acc, group) => {
    const keepId = keepSelection[groupKey(group)];
    return acc + group.documents.filter((d) => d.id !== keepId).length;
  }, 0);

  const siteOptions = [
    { key: "all", value: "", text: t("all_sites") || "Todos los sitios" },
    ...sites.map((s) => ({ key: s._id, value: s._id, text: s.name })),
  ];

  return (
    <div className="duplicate-periods-review">
      <ToastContainer />

      <p className="duplicate-periods-review__description">
        {t("duplicate_periods_description") ||
          "Elegí qué versión conservar de cada período duplicado antes de borrar el resto."}
      </p>

      <div className="duplicate-periods-review__filters">
        <label>{t("site") || "Sitio"}</label>
        <Dropdown
          selection
          search
          options={siteOptions}
          value={selectedSite}
          onChange={(_, data) => setSelectedSite(data.value)}
        />
      </div>

      {loading ? (
        <div className="duplicate-periods-review__loading">
          <Loader active inline="centered" />
        </div>
      ) : groups.length === 0 ? (
        <div className="duplicate-periods-review__empty">
          <Icon name="check circle outline" />
          <p>{t("no_duplicates_found") || "No se encontraron períodos duplicados."}</p>
        </div>
      ) : (
        <>
          <div className="duplicate-periods-review__groups">
            {groups.map((group) => {
              const gk = groupKey(group);
              const keepId = keepSelection[gk];
              const { toMerge, conflicts } = computeMergePreview(group, keepId);

              return (
                <div key={gk} className="duplicate-periods-review__group">
                  <div className="duplicate-periods-review__group-header">
                    <span className="module-badge">{group.moduleLabel}</span>
                    <span className="site-name">{group.siteName}</span>
                    <span className="period">{group.year} · {group.period}</span>
                  </div>

                  <div className="duplicate-periods-review__table-wrapper">
                    <Table className="dpr-table" unstackable>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell textAlign="center">{t("keep") || "Conservar"}</Table.HeaderCell>
                          <Table.HeaderCell>ID</Table.HeaderCell>
                          <Table.HeaderCell textAlign="center">{t("filled_fields") || "Campos cargados"}</Table.HeaderCell>
                          <Table.HeaderCell>{t("date") || "Fecha"}</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {group.documents.map((doc) => {
                          const isKeeper = doc.id === keepId;
                          return (
                            <Table.Row key={doc.id} className={isKeeper ? "dpr-row--keep" : "dpr-row--delete"}>
                              <Table.Cell textAlign="center">
                                <Radio
                                  checked={isKeeper}
                                  onChange={() => handleSelectKeep(group, doc.id)}
                                />
                              </Table.Cell>
                              <Table.Cell className="dpr-id-cell">{doc.id}</Table.Cell>
                              <Table.Cell textAlign="center">
                                {doc.fields && doc.fields.length > 0 ? (
                                  <Popup
                                    on="click"
                                    position="left center"
                                    trigger={
                                      <span className="dpr-fields-trigger">
                                        {doc.filledFields} <Icon name="eye" size="small" />
                                      </span>
                                    }
                                    content={
                                      <div className="dpr-fields-popup">
                                        {doc.fields.map((f) => (
                                          <div key={f.name} className="dpr-fields-popup__row">
                                            <span className="field-name">{t(f.name) || f.name}</span>
                                            <span className="field-value">{f.value} {f.unit}</span>
                                          </div>
                                        ))}
                                      </div>
                                    }
                                  />
                                ) : (
                                  doc.filledFields
                                )}
                              </Table.Cell>
                              <Table.Cell>{formatDate(doc.date)}</Table.Cell>
                            </Table.Row>
                          );
                        })}
                      </Table.Body>
                    </Table>
                  </div>

                  {toMerge.length > 0 && (
                    <div className="dpr-merge-note">
                      <Icon name="magic" />
                      {(t("fields_to_merge_msg") || "Se van a completar {n} campo(s) vacíos del conservado: {fields}")
                        .replace("{n}", toMerge.length)
                        .replace("{fields}", toMerge.map((m) => t(m.field) || m.field).join(", "))}
                    </div>
                  )}

                  {conflicts.length > 0 && (
                    <div className="dpr-conflicts">
                      <div className="dpr-conflicts__header">
                        <Icon name="warning sign" color="orange" />
                        {t("conflicts_found_msg") ||
                          "Estos campos tienen valores distintos entre los duplicados -- elegí cuál conservar:"}
                      </div>
                      {conflicts.map((c) => {
                        const keeperOption = c.options.find((o) => o.docId === keepId);
                        const current =
                          conflictResolutions[gk]?.[c.field] || keeperOption || c.options[0];
                        const options = c.options.map((o) => ({
                          key: o.docId,
                          value: JSON.stringify({ value: o.value, unit: o.unit }),
                          text: `${o.value} ${o.unit} (${o.docId.slice(-6)}${o.docId === keepId ? " · " + (t("keep") || "conservado") : ""})`,
                        }));
                        return (
                          <div key={c.field} className="dpr-conflicts__row">
                            <span className="field-name">{t(c.field) || c.field}</span>
                            <Dropdown
                              selection
                              compact
                              options={options}
                              value={JSON.stringify({ value: current.value, unit: current.unit })}
                              onChange={(_, data) => handleResolveConflict(group, c.field, JSON.parse(data.value))}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="duplicate-periods-review__actions">
            <Button
              className="dpr-delete-btn"
              onClick={handleDelete}
              disabled={deleting || totalToDelete === 0}
              loading={deleting}
            >
              <Icon name="trash" />
              {t("delete_selected_duplicates") || "Borrar duplicados"} ({totalToDelete})
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
